import blogIndex from '../data/blog-index.json';

const POSTS_PER_PAGE = 12;

export function renderBlogCatalog(container) {
  if (!container) return;

  let currentPage = 1;
  let searchQuery = '';
  let selectedCategory = 'All';

  // Extract unique categories
  const categoriesSet = new Set();
  blogIndex.forEach(p => {
    (p.categories || []).forEach(c => {
      if (c && c !== 'Uncategorized') categoriesSet.add(c);
    });
  });
  // Sort and pick top prominent categories
  const topCategories = ['All', 'Life', 'Family', 'Faith', 'Relationships', 'Devotional', 'General'];

  function render() {
    const filtered = blogIndex.filter(post => {
      const matchesSearch = searchQuery === '' ||
        post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (post.categories || []).some(c => c.toLowerCase().includes(searchQuery.toLowerCase()));

      if (!matchesSearch) return false;

      if (selectedCategory === 'All') return true;
      return (post.categories || []).includes(selectedCategory);
    });

    const totalPages = Math.ceil(filtered.length / POSTS_PER_PAGE) || 1;
    if (currentPage > totalPages) currentPage = 1;

    const startIdx = (currentPage - 1) * POSTS_PER_PAGE;
    const paginated = filtered.slice(startIdx, startIdx + POSTS_PER_PAGE);

    container.innerHTML = `
      <div class="blog-page">
        <!-- Blog Header -->
        <header class="blog-hero">
          <div class="wt-container text-center">
            <div class="studies-brand-pill" style="background:rgba(245, 158, 11, 0.15); color:var(--accent-gold); border-color:rgba(245, 158, 11, 0.3);">
              <i class="fa-solid fa-feather-pointed"></i>
              <span>Walk Together Blog</span>
            </div>
            <h1 class="studies-title">Articles & Reflections</h1>
            <p class="studies-subtitle">
              Biblical reflections, personal stories, discipleship insights, and family encouragement from over a decade of walking with Christ.
            </p>

            <!-- Search & Filter Controls -->
            <div class="studies-controls">
              <div class="studies-search-box">
                <i class="fa-solid fa-magnifying-glass search-icon"></i>
                <input 
                  type="text" 
                  id="blog-search-input" 
                  placeholder="Search 335+ blog articles..." 
                  value="${searchQuery}" 
                />
                ${searchQuery ? `<button id="blog-clear-search" class="clear-btn">&times;</button>` : ''}
              </div>

              <div class="studies-filter-chips">
                ${topCategories.map(cat => `
                  <button class="filter-chip ${selectedCategory === cat ? 'active' : ''}" data-category="${cat}">
                    ${cat} ${cat === 'All' ? `(${blogIndex.length})` : ''}
                  </button>
                `).join('')}
              </div>
            </div>
          </div>
        </header>

        <!-- Blog Posts Grid -->
        <main class="wt-container blog-grid-container">
          <div class="blog-results-meta">
            <span>Showing <strong>${filtered.length > 0 ? startIdx + 1 : 0}-${Math.min(startIdx + POSTS_PER_PAGE, filtered.length)}</strong> of <strong>${filtered.length}</strong> posts</span>
            ${searchQuery || selectedCategory !== 'All' ? `
              <button id="blog-reset-filters" class="btn-text"><i class="fa-solid fa-xmark"></i> Clear filters</button>
            ` : ''}
          </div>

          ${paginated.length === 0 ? `
            <div class="studies-empty text-center">
              <i class="fa-solid fa-newspaper empty-icon"></i>
              <h3>No articles found</h3>
              <p>We couldn't find any articles matching your search or filters.</p>
              <button class="btn-primary" id="blog-empty-reset">Reset Search</button>
            </div>
          ` : `
            <div class="blog-grid">
              ${paginated.map(post => `
                <article class="blog-card">
                  <div class="blog-card-meta">
                    <span class="blog-date"><i class="fa-regular fa-calendar"></i> ${post.formatted_date}</span>
                    <span class="blog-read-time"><i class="fa-regular fa-clock"></i> ${post.reading_time}</span>
                  </div>

                  <h2 class="blog-card-title">
                    <a href="#blog/${post.slug}">${post.title}</a>
                  </h2>

                  <p class="blog-card-excerpt">
                    ${post.excerpt}
                  </p>

                  <div class="blog-card-footer">
                    <div class="blog-category-tags">
                      ${(post.categories || []).slice(0, 2).map(c => `<span class="blog-badge">${c}</span>`).join('')}
                    </div>
                    <a href="#blog/${post.slug}" class="blog-read-link">
                      Read article <i class="fa-solid fa-arrow-right"></i>
                    </a>
                  </div>
                </article>
              `).join('')}
            </div>

            <!-- Pagination Bar -->
            ${totalPages > 1 ? `
              <nav class="blog-pagination" aria-label="Blog pagination">
                <button class="pagination-btn" id="page-prev" ${currentPage === 1 ? 'disabled' : ''}>
                  <i class="fa-solid fa-chevron-left"></i> Previous
                </button>
                <div class="pagination-pages">
                  ${generatePaginationNumbers(currentPage, totalPages)}
                </div>
                <button class="pagination-btn" id="page-next" ${currentPage === totalPages ? 'disabled' : ''}>
                  Next <i class="fa-solid fa-chevron-right"></i>
                </button>
              </nav>
            ` : ''}
          `}
        </main>
      </div>
    `;

    // Event handlers
    const searchInput = document.getElementById('blog-search-input');
    if (searchInput) {
      searchInput.addEventListener('input', (e) => {
        searchQuery = e.target.value;
        currentPage = 1;
        render();
        const inputNow = document.getElementById('blog-search-input');
        if (inputNow) {
          inputNow.focus();
          inputNow.selectionStart = inputNow.selectionEnd = searchQuery.length;
        }
      });
    }

    const clearBtn = document.getElementById('blog-clear-search');
    if (clearBtn) {
      clearBtn.onclick = () => {
        searchQuery = '';
        currentPage = 1;
        render();
      };
    }

    const resetBtn = document.getElementById('blog-reset-filters');
    if (resetBtn) {
      resetBtn.onclick = () => {
        searchQuery = '';
        selectedCategory = 'All';
        currentPage = 1;
        render();
      };
    }

    const emptyReset = document.getElementById('blog-empty-reset');
    if (emptyReset) {
      emptyReset.onclick = () => {
        searchQuery = '';
        selectedCategory = 'All';
        currentPage = 1;
        render();
      };
    }

    const filterBtns = container.querySelectorAll('.filter-chip');
    filterBtns.forEach(btn => {
      btn.onclick = () => {
        selectedCategory = btn.dataset.category;
        currentPage = 1;
        render();
      };
    });

    const prevBtn = document.getElementById('page-prev');
    if (prevBtn) {
      prevBtn.onclick = () => {
        if (currentPage > 1) {
          currentPage--;
          render();
          window.scrollTo({ top: 300, behavior: 'smooth' });
        }
      };
    }

    const nextBtn = document.getElementById('page-next');
    if (nextBtn) {
      nextBtn.onclick = () => {
        if (currentPage < totalPages) {
          currentPage++;
          render();
          window.scrollTo({ top: 300, behavior: 'smooth' });
        }
      };
    }

    const pageNumBtns = container.querySelectorAll('.page-num-btn');
    pageNumBtns.forEach(btn => {
      btn.onclick = () => {
        currentPage = parseInt(btn.dataset.page, 10);
        render();
        window.scrollTo({ top: 300, behavior: 'smooth' });
      };
    });
  }

  function generatePaginationNumbers(current, total) {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, current - 2);
    let end = Math.min(total, start + maxVisible - 1);

    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }

    if (start > 1) {
      pages.push(`<button class="page-num-btn" data-page="1">1</button>`);
      if (start > 2) pages.push(`<span class="page-ellipsis">...</span>`);
    }

    for (let i = start; i <= end; i++) {
      pages.push(`
        <button class="page-num-btn ${i === current ? 'active' : ''}" data-page="${i}">
          ${i}
        </button>
      `);
    }

    if (end < total) {
      if (end < total - 1) pages.push(`<span class="page-ellipsis">...</span>`);
      pages.push(`<button class="page-num-btn" data-page="${total}">${total}</button>`);
    }

    return pages.join('');
  }

  render();
}
